/**
 * Visitor geolocation helper.
 *
 * On the first page visit we call `/api/geo/detect` (server-side IP lookup
 * via ip-api.com) to figure out the user's country + default currency, and
 * cache the result in localStorage so subsequent visits are instant.
 *
 * Manual overrides (anywhere we write `aduku_country` / `aduku_currency`
 * from a user-driven UI control) always win — `detectGeoOnce` never
 * overwrites an existing value.
 */

const COUNTRY_KEY = "aduku_country";
const CURRENCY_KEY = "aduku_currency";
const CITY_KEY = "aduku_city";
const COUNTRY_NAME_KEY = "aduku_country_name";
const DETECTED_FLAG = "aduku_geo_detected";

const API = process.env.REACT_APP_BACKEND_URL;

export function getStoredCountry() {
    try { return localStorage.getItem(COUNTRY_KEY) || ""; } catch { return ""; }
}

export function getStoredCurrency() {
    try { return localStorage.getItem(CURRENCY_KEY) || ""; } catch { return ""; }
}

export function getStoredCity() {
    try { return localStorage.getItem(CITY_KEY) || ""; } catch { return ""; }
}

export function getStoredCountryName() {
    try { return localStorage.getItem(COUNTRY_NAME_KEY) || ""; } catch { return ""; }
}

export function setStoredGeo(country, currency, opts = {}) {
    try {
        if (country) localStorage.setItem(COUNTRY_KEY, country);
        if (currency) localStorage.setItem(CURRENCY_KEY, currency);
        if (opts.city) localStorage.setItem(CITY_KEY, opts.city);
        if (opts.country_name) localStorage.setItem(COUNTRY_NAME_KEY, opts.country_name);
    } catch { /* private mode — silently skip */ }
}

/**
 * Fire-and-forget IP detection. Runs once per browser unless the user
 * clears storage. Never blocks UI; returns the resolved geo or null.
 */
export async function detectGeoOnce() {
    try {
        if (localStorage.getItem(DETECTED_FLAG)) return null;
        // If a manual override already exists, just mark as detected and skip.
        if (getStoredCountry() && getStoredCurrency()) {
            localStorage.setItem(DETECTED_FLAG, "1");
            return null;
        }
        const r = await fetch(`${API}/api/geo/detect`);
        if (!r.ok) return null;
        const data = await r.json();
        if (data?.country && data?.currency) {
            setStoredGeo(data.country, data.currency, { city: data.city, country_name: data.country_name });
            localStorage.setItem(DETECTED_FLAG, "1");
            return data;
        }
    } catch { /* offline / blocked — quiet */ }
    return null;
}
